import Button from '@components/v2/ui/Button/Button';
import PopoverTabs, { PopoverTab } from '@components/v2/ui/PopoverTabs/PopoverTabs';
import { Divider, Stack } from '@mui/material';
import { Grid2x2, List } from 'lucide-react';
import LangEnum from '../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { CATALOG_CARD_SIZE_VARIANTS } from '../../sizing/catalogCardSize.consts';
import { DEFAULT_CATALOG_VIEW } from '../../view/catalogView.consts';
import { CatalogViewDraft, CatalogViewMode, CatalogViewOption } from '../../view/catalogView.types';
import { CatalogViewSettingsContentProps } from './catalogViewSettings.types';
import CatalogViewApplyButton from './components/CatalogViewApplyButton';
import CatalogViewGroupSection from './components/CatalogViewGroupSection';
import CatalogViewSettingsRow from './components/CatalogViewSettingsRow';
import CatalogViewSortSection from './components/CatalogViewSortSection';
import CatalogViewValueSelect from './components/CatalogViewValueSelect';

const CARD_SIZE_OPTIONS: CatalogViewOption<CatalogViewDraft['cardSize']>[] =
  CATALOG_CARD_SIZE_VARIANTS.map((variant) => ({ value: variant.key, label: variant.label }));

const LOADING_DRAFT: CatalogViewDraft = {
  ...DEFAULT_CATALOG_VIEW,
  isRestorePending: false,
};

const LOADING_ENABLEMENT: CatalogViewSettingsContentProps['enablement'] = {
  isCombinationInvalid: false,
  isSortTargetMissing: false,
  isGroupTargetMissing: false,
  canApplyForMe: false,
  canApplyForAll: false,
  canRestore: false,
};

const getIsTargetMissing = (enabled: CatalogViewSettingsContentProps['enablement']): boolean =>
  enabled.isSortTargetMissing || enabled.isGroupTargetMissing;

const resolveBlockedForMeReason = (
  enabled: CatalogViewSettingsContentProps['enablement'],
): LangEnum | undefined => {
  if (enabled.isCombinationInvalid) {
    return LangEnum.CATALOGS_VIEW_MANUAL_REQUIRES_MANUAL_GROUP;
  }

  if (enabled.isGroupTargetMissing) {
    return LangEnum.API_EXCEPTION_CATALOG_GROUP_TARGET_REQUIRED;
  }

  if (enabled.isSortTargetMissing) {
    return LangEnum.CATALOGS_VIEW_SORT_TARGET_REQUIRED;
  }

  return undefined;
};

const CatalogViewSettingsContent = ({
  draft,
  isOfflineMode,
  notGroupedWarning,
  visibility,
  enablement,
  onModeChange,
  onGroupByChange,
  onGroupTargetIdChange,
  onSortCriterionChange,
  onSortTargetIdChange,
  onCardSizeChange,
  onRestore,
  onApplyForMe,
  onApplyForAll,
}: CatalogViewSettingsContentProps) => {
  const { translate } = useTranslation();

  const view = draft ?? LOADING_DRAFT;
  const enabled = draft ? enablement : LOADING_ENABLEMENT;
  const isLoading = draft === null;

  const handleModeChange = (mode: string) => {
    if (isOfflineMode && mode === CatalogViewMode.LIST) {
      return;
    }

    onModeChange(mode as CatalogViewMode);
  };

  return (
    <Stack spacing={1}>
      <PopoverTabs
        value={isOfflineMode ? CatalogViewMode.CARD : view.mode}
        variant="fullWidth"
        testId={CatalogProductsTestIds.VIEW_TABS}
        onChange={handleModeChange}
      >
        <PopoverTab
          value={CatalogViewMode.LIST}
          label={translate(LangEnum.GLOBAL_LIST)}
          icon={List}
          disabled={isLoading}
          testId={CatalogProductsTestIds.VIEW_TAB_LIST}
          blockedReason={
            isOfflineMode ? translate(LangEnum.CATALOGS_VIEW_LIST_OFFLINE_UNAVAILABLE) : undefined
          }
        />
        <PopoverTab
          value={CatalogViewMode.CARD}
          label={translate(LangEnum.GLOBAL_CARD)}
          icon={Grid2x2}
          disabled={isLoading}
          testId={CatalogProductsTestIds.VIEW_TAB_CARD}
        />
      </PopoverTabs>

      {visibility.showSortConfig && (
        <CatalogViewSortSection
          sortCriterion={view.sortCriterion}
          groupBy={view.groupBy}
          sortTargetId={view.sortTargetId}
          isLoading={isLoading}
          isSortTargetMissing={enabled.isSortTargetMissing}
          onSortCriterionChange={onSortCriterionChange}
          onSortTargetIdChange={onSortTargetIdChange}
        />
      )}

      {visibility.showGroupConfig && (
        <CatalogViewGroupSection
          groupBy={view.groupBy}
          sortCriterion={view.sortCriterion}
          groupTargetId={view.groupTargetId}
          isLoading={isLoading}
          isGroupTargetMissing={enabled.isGroupTargetMissing}
          notGroupedWarning={visibility.showCatalogConfig ? notGroupedWarning : null}
          onGroupByChange={onGroupByChange}
          onGroupTargetIdChange={onGroupTargetIdChange}
        />
      )}

      {visibility.showCardSizeConfig && (
        <CatalogViewSettingsRow label={LangEnum.CATALOGS_VIEW_CARD_SIZE}>
          <CatalogViewValueSelect
            label={LangEnum.CATALOGS_VIEW_CARD_SIZE}
            value={view.cardSize}
            options={CARD_SIZE_OPTIONS}
            testId={CatalogProductsTestIds.VIEW_CARD_SIZE_SELECT}
            disabled={isLoading}
            onChange={onCardSizeChange}
          />
        </CatalogViewSettingsRow>
      )}

      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Button
          size="small"
          variant="text"
          color="secondary"
          disabled={!enabled.canRestore}
          testId={CatalogProductsTestIds.VIEW_RESTORE_BUTTON}
          onClick={onRestore}
        >
          {translate(LangEnum.GLOBAL_RESTORE)}
        </Button>
        <CatalogViewApplyButton
          hasScopeChoice={visibility.showCatalogConfig}
          isTargetMissing={getIsTargetMissing(enabled)}
          canApplyForMe={enabled.canApplyForMe}
          blockedForMeReason={resolveBlockedForMeReason(enabled)}
          canApplyForAll={enabled.canApplyForAll}
          onApplyForMe={onApplyForMe}
          onApplyForAll={onApplyForAll}
        />
      </Stack>
    </Stack>
  );
};

export default CatalogViewSettingsContent;
